import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import {
	breakpoint,
	colorContrastLower,
	gridGap,
	spacingStaticSm,
	spacingStaticXs,
} from "@porsche-design-system/components-react/emotion"
import type { JSX } from "react"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { shouldForwardProp } from "src/common/shouldForwardProp"

export interface ListItemProps {
	/**
	 * Sets the label of the ListItem.
	 * The value can be a JSX Element or a string.
	 */
	label?: JSX.Element | string

	/**
	 * Prevents the label from wrapping into a multiline
	 */
	labelSingleLine?: boolean

	/**
	 * Sets the value of the ListItem.
	 * Value can be a JSX Element or a string. It also can be a boolean
	 * which gets displayed as a checkmark or cancel icon. Undefined will
	 * be displayed as an placeholder icon.
	 */
	value?: JSX.Element | string

	/**
	 * Prevents the value from wrapping into a multiline
	 */
	valueSingleLine?: boolean

	/**
	 * Applied to the value element (not the row), so tests can target the value in isolation
	 * without also matching the label.
	 */
	"data-testid"?: string
}

export const ListItem = (props: ListItemProps) => {
	const { "data-testid": dataTestId, label, labelSingleLine, value, valueSingleLine, ...rest } = props

	return (
		<Row {...rest}>
			<Label title={typeof label === "string" ? label : undefined} $singleLine={labelSingleLine}>
				{label}
			</Label>
			{typeof value === "string" ? (
				<TextLabel
					title={value === emptyPlaceholder ? undefined : value}
					weight="semibold"
					ellipsis={valueSingleLine}
					data-testid={dataTestId}
				>
					{value}
				</TextLabel>
			) : (
				<Label $singleLine={valueSingleLine} data-testid={dataTestId}>
					{value}
				</Label>
			)}
		</Row>
	)
}

const Row = styled.li`
	display: grid;
	margin: 0;
	hyphens: initial;
	border-bottom: 1px solid ${colorContrastLower};
	list-style-type: none;

	&:first-of-type {
		margin-top: -${spacingStaticSm};
	}

	@media (max-width: ${breakpoint.xs}px) {
		padding: ${spacingStaticSm} 0;
	}

	@media (min-width: ${breakpoint.xs}px) {
		gap: ${gridGap};
		grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	}
`

// padding on inner child to prevent cropped hover effect of edit buttons using p-button-pure
// on mobile it is still cropped, but there we don't have hover events
//
// for $singleLine, overflow: hidden clips at the padding edge, so links/buttons with an
// expanding hover effect (e.g. p-link-pure) near the start/end of the truncated text get cropped
// horizontally too. Adding inline padding pushes the overflow-hidden boundary outward to leave
// room for the hover effect, while the matching negative inline margin keeps the visible
// text/column alignment unchanged.
const Label = styled("div", { shouldForwardProp })<{ $singleLine?: boolean }>`
	min-width: 0;
	${(props) =>
		props.$singleLine &&
		css`
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			padding-inline: ${spacingStaticXs};
			margin-inline: -${spacingStaticXs};
		`};

	@media (min-width: ${breakpoint.xs}px) {
		padding-block: ${spacingStaticSm};
	}
`

const TextLabel = Label.withComponent(PText)
